import { createEffect, createMemo, createSignal, onCleanup, onMount, ParentComponent } from 'solid-js'
import { useNavigate } from '@solidjs/router'
import { useBreakpoint } from './useBreakpoint'
import { TopBar } from './TopBar'
import { Sidebar } from './Sidebar'
import { SidebarToggle } from './Sidebar'
import { MobileDrawer } from './MobileDrawer'
import { BottomTabBar } from './BottomTabBar'
import { subscribe, unsubscribe } from '/web/store/socket'
import { userStore } from '/web/store'
import { EVENTS, events } from '/web/emitter'
import './nav.css'

export const AppShell: ParentComponent = (props) => {
  const bp = useBreakpoint()
  const navigate = useNavigate()

  // Redirect home whenever the user logs out, from any logout button in the app
  // (the loggedOut event fires only on an explicit logout, not on auth state flips).
  const onLoggedOut = () => navigate('/')
  const [drawerOpen, setDrawerOpen] = createSignal(false)
  const [sidebarOverride, setSidebarOverride] = createSignal<'expanded' | 'collapsed' | null>(null)

  const tokensChangedValidator = { userId: 'string', tokens: 'number', hasClaimedInitialTokens: 'boolean?' } as const

  const handleTokensChanged = (body: { userId: string; tokens: number; hasClaimedInitialTokens?: boolean }) => {
    const user = userStore.getState()
    if (user.profile) {
      userStore.setState({
        profile: {
          ...user.profile,
          totalAvailableTokens: body.tokens,
          hasClaimedInitialTokens: body.hasClaimedInitialTokens ?? user.profile.hasClaimedInitialTokens,
        },
      })
    }
  }

  // Load AIFans subscriptions once whenever the user is logged in, so premium state
  // (Member pill in the top bar, Tokens tab in the bottom nav) is available app-wide —
  // not only on pages that fetch it (Model/Subscriptions). Gated on a memo of `loggedIn`
  // so it fires only when auth state flips, not on every store update.
  const loggedIn = createMemo(() => userStore().loggedIn)
  createEffect(() => {
    if (loggedIn()) {
      userStore.loadAIFansSubscriptions()
    }
  })

  onMount(() => {
    subscribe('profile-tokens-changed', tokensChangedValidator, handleTokensChanged)
    events.on(EVENTS.loggedOut, onLoggedOut)
  })

  onCleanup(() => {
    unsubscribe('profile-tokens-changed', handleTokensChanged)
    events.removeListener(EVENTS.loggedOut, onLoggedOut)
  })

  const sidebarClass = () => {
    const override = sidebarOverride()
    if (bp() === 'desktop') {
      return override === 'collapsed' ? 'cf-sidebar-collapsed' : ''
    }
    if (bp() === 'tablet') {
      return override === 'expanded' ? 'cf-sidebar-expanded' : ''
    }
    return ''
  }

  const toggleSidebar = () => {
    const override = sidebarOverride()
    if (bp() === 'desktop') {
      setSidebarOverride(override === 'collapsed' ? null : 'collapsed')
    } else if (bp() === 'tablet') {
      setSidebarOverride(override === 'expanded' ? null : 'expanded')
    }
  }

  const sidebarCollapsed = () => {
    if (bp() === 'desktop') return sidebarOverride() === 'collapsed'
    if (bp() === 'tablet') return sidebarOverride() !== 'expanded'
    return true
  }

  return (
    <div class={`cf-app ${sidebarClass()}`}>
      <TopBar onOpenDrawer={() => setDrawerOpen(true)} />
      <Sidebar collapsed={sidebarCollapsed()} onToggle={toggleSidebar} />
      <SidebarToggle collapsed={sidebarCollapsed()} onToggle={toggleSidebar} />
      <MobileDrawer open={drawerOpen()} onClose={() => setDrawerOpen(false)} />
      <div class="cf-main">{props.children}</div>
      <BottomTabBar />
    </div>
  )
}
